import Tooltip from 'components/@extended/Tooltip';
import { buttonStyle } from 'components/buttons/buttonStyle';
import DecimalInput from 'components/forms/inputs/rhf-inputs/DecimalInput';
import { Button } from 'components/ui/button';
import { mapInvoiceMatterFormDataToAdjustmentDto } from 'pages/invoices/util/InvoicesPageUtil';
import React, { useCallback } from 'react';
import { Control, UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import NumberFormat from 'react-number-format';
import { useSelector } from 'store';
import { useUpdateInvoiceMatterAdjustmentsMutation } from 'store/api/invoice-details';
import { InvoiceMatterForm, InvoiceMatterSummary, InvoiceTimecardAssignment } from 'types/invoice-details';
import { InvoiceData, InvoiceType } from 'types/invoices';
import { errorAlert } from 'utils/alert';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { paymentCurrencyInputClassName, usePaymentCurrencyMode } from 'utils/usePaymentCurrencyMode';

/** A cap figure's label, beside its value. */
const CAP_LABEL = 'text-fg-secondary mr-[5px] font-normal';

interface Props {
    index: number;
    assignment: InvoiceTimecardAssignment;
    invoice: InvoiceData;
    control: Control<InvoiceMatterForm>;
    invoiceMatter: InvoiceMatterSummary;
    getValues: UseFormGetValues<InvoiceMatterForm>;
    setValue: UseFormSetValue<InvoiceMatterForm>;
    isInViewMode: boolean;
}
const InvoiceAssignmentTimecardHeader: React.FC<Props> = (props) => {
    const { index, assignment, invoice, getValues, setValue, control, invoiceMatter, isInViewMode } = props;
    const { calculationLog } = invoiceMatter;
    const { id: invoiceId, invoiceTypeCode } = invoice;
    const { isPaymentCurrencyMode } = usePaymentCurrencyMode(invoice);
    const log = isPaymentCurrencyMode ? calculationLog?.paymentCurrency : calculationLog;
    const { buttonsDisabled: areButtonsDisabled } = useSelector((state) => state.buttons);

    const otherAssignments = (invoiceMatter.invoiceAssignments || []).filter(
        (item) => item.assignmentId !== assignment.assignmentId
    );
    const feesForCappedAssignments = otherAssignments.reduce((acc, item) => acc + (item.capAdjustment ?? 0), 0);
    const assignmentFeesBeforeAdjustments = assignment.totalBeforeAdjustments ?? 0;
    const feesBeforeAdjustments = otherAssignments.reduce((acc, item) => acc + (item.totalBeforeAdjustments ?? 0), 0);

    const expensesWithVatBeforeAdjustments = log?.expensesWithVat?.totalBeforeMatterAdjustments ?? 0;
    const expensesWithoutVatBeforeAdjustments = log?.expensesWithoutVat?.totalBeforeMatterAdjustments ?? 0;

    const [updateInvoiceMatterAdjustments] = useUpdateInvoiceMatterAdjustmentsMutation();

    const setValuesProportionally = useCallback(
        (groupAdjustment: number, sumAdjustmentCap: number) => {
            const totalBeforeAdjustments =
                expensesWithVatBeforeAdjustments +
                expensesWithoutVatBeforeAdjustments +
                feesBeforeAdjustments +
                sumAdjustmentCap;
            const proportionFee =
                totalBeforeAdjustments === 0 ? 0 : (feesBeforeAdjustments + sumAdjustmentCap) / totalBeforeAdjustments;
            const proportionExpensesWithoutVat =
                totalBeforeAdjustments === 0 ? 0 : expensesWithoutVatBeforeAdjustments / totalBeforeAdjustments;
            const proportionExpensesWithVat =
                totalBeforeAdjustments === 0 ? 0 : expensesWithVatBeforeAdjustments / totalBeforeAdjustments;

            setValue(
                'adjustmentAmountForExpensesWithVat',
                Math.round((groupAdjustment * proportionExpensesWithVat - expensesWithVatBeforeAdjustments) * 100) / 100
            );
            setValue(
                'adjustmentAmountForExpensesWithoutVat',
                Math.round(
                    (groupAdjustment * proportionExpensesWithoutVat - expensesWithoutVatBeforeAdjustments) * 100
                ) / 100
            );
            setValue(
                'adjustmentAmountForFees',
                Math.round((groupAdjustment * proportionFee - (feesBeforeAdjustments + sumAdjustmentCap)) * 100) / 100
            );
        },
        [expensesWithVatBeforeAdjustments, expensesWithoutVatBeforeAdjustments, feesBeforeAdjustments, setValue]
    );

    const handleOnCapAdjustmentChange = useCallback(() => {
        const appliedCap = getValues('adjustmentAmount');

        if (!appliedCap && appliedCap !== 0) {
            setValue('adjustmentAmountForExpensesWithVat', null);
            setValue('adjustmentAmountForExpensesWithoutVat', null);
            setValue('adjustmentAmountForFees', null);
            return;
        }

        const areCostsCapped = getValues('areCostsCapped');
        const capAdjustment = getValues(`invoiceAssignments.${index}.capAdjustment`);

        const allFeesForCappedAssignments = feesForCappedAssignments + Number(capAdjustment);
        const groupAdjustment = appliedCap < allFeesForCappedAssignments ? 0 : appliedCap - allFeesForCappedAssignments;
        const sumAdjustmentCap = !capAdjustment && capAdjustment !== 0 ? assignmentFeesBeforeAdjustments : 0;

        if (areCostsCapped) {
            setValuesProportionally(groupAdjustment, sumAdjustmentCap);
        } else {
            setValue('adjustmentAmountForExpensesWithVat', null);
            setValue('adjustmentAmountForExpensesWithoutVat', null);
            setValue(
                'adjustmentAmountForFees',
                Math.round((groupAdjustment - (feesBeforeAdjustments + sumAdjustmentCap)) * 100) / 100
            );
        }
    }, [
        assignmentFeesBeforeAdjustments,
        feesBeforeAdjustments,
        feesForCappedAssignments,
        getValues,
        index,
        setValue,
        setValuesProportionally,
    ]);

    const onSubmit = async (): Promise<void> => {
        const formData: InvoiceMatterForm = getValues();

        try {
            const data = mapInvoiceMatterFormDataToAdjustmentDto(formData, invoiceId);
            await updateInvoiceMatterAdjustments({ ...data })
                .unwrap()
                .catch(console.error);
        } catch (e) {
            console.error('onSubmit error: ', e);
            errorAlert(t('forms.error-submitting-the-form'));
        }
    };

    const submitButton = (
        <Button
            {...buttonStyle({ variant: 'outlined', size: 'extraSmall' }, 'hover:border-divider hover:bg-grey-100')}
            onClick={() => {
                handleOnCapAdjustmentChange();
                onSubmit();
            }}
        >
            {t('save')}
        </Button>
    );

    return !assignment.assignmentId || invoiceTypeCode === InvoiceType.CREDIT ? (
        <></>
    ) : (
        <div className="border-divider flex min-h-[38px] flex-col border-b px-4 py-2">
            <div className="flex items-center justify-between">
                <div>
                    <FormattedMessage id="invoices.details.assignments.assignment" />
                    <span className="text-fg hover:text-link ml-[0.5ch] cursor-pointer font-semibold hover:underline">
                        {assignment.assignmentName}
                    </span>
                </div>
                <div className="text-subtitle2 flex flex-nowrap items-center justify-end overflow-hidden">
                    <Tooltip text="invoices.details.assignments.total-cap-tooltip">
                        <div className="mr-[15px] flex">
                            <div className={CAP_LABEL}>
                                <FormattedMessage id="invoices.details.assignments.total-cap" />
                            </div>
                            <NumberFormat
                                displayType="text"
                                fixedDecimalScale
                                decimalScale={2}
                                value={assignment.totalCap || 0}
                            />
                        </div>
                    </Tooltip>
                    <Tooltip text="invoices.details.assignments.remaining-cap-tooltip">
                        <div className="mr-[15px] flex">
                            <div className={CAP_LABEL}>
                                <FormattedMessage id="invoices.details.assignments.remaining-cap" />
                            </div>
                            <NumberFormat
                                displayType="text"
                                fixedDecimalScale
                                decimalScale={2}
                                value={assignment.remainingCap || 0}
                            />
                        </div>
                    </Tooltip>
                    <Tooltip text="invoices.details.assignments.cap-adjustment-tooltip">
                        <div className={CAP_LABEL}>
                            <FormattedMessage id="invoices.details.assignments.cap-adjustment" />
                        </div>
                    </Tooltip>
                    <div className="flex items-center font-normal">
                        <DecimalInput
                            control={control}
                            name={`invoiceAssignments.${index}.capAdjustment`}
                            gap={0}
                            wrapperClassName={cn('flex grow', isPaymentCurrencyMode && paymentCurrencyInputClassName)}
                            suffixComponent={isInViewMode ? undefined : submitButton}
                            disabled={isInViewMode || areButtonsDisabled}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InvoiceAssignmentTimecardHeader;
